import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import './deployments.css';

interface ConfirmDialogProps {
  show: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  /** `danger` for an action that cannot be undone. */
  variant?: 'primary' | 'danger';
  /** The person must type this exactly before the action is allowed. */
  typeToConfirm?: string;
  /** Ask for an optional note with this label. The note is passed to `onConfirm`. */
  noteLabel?: string;
  notePlaceholder?: string;
  busy?: boolean;
  error?: string | null;
  /** The action is not possible right now: the reason, shown in place of the confirm step. */
  blocked?: string;
  onConfirm: (note: string) => void;
  onCancel: () => void;
}

/** The one confirmation step for every deploy action that changes something. */
export function ConfirmDialog({
  show,
  title,
  children,
  confirmLabel,
  variant = 'primary',
  typeToConfirm,
  noteLabel,
  notePlaceholder,
  busy = false,
  error,
  blocked,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (show) {
      setTyped('');
      setNote('');
    }
  }, [show]);

  const typedOk = !typeToConfirm || typed === typeToConfirm;

  return (
    <Modal show={show} onHide={busy ? undefined : onCancel} centered>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="deploy-confirm">
        {children}
        {typeToConfirm && !blocked && (
          <Form.Group className="mt-3">
            <Form.Label>
              Type <span className="mono">{typeToConfirm}</span> to confirm
            </Form.Label>
            <Form.Control
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              disabled={busy}
            />
          </Form.Group>
        )}
        {noteLabel && !blocked && (
          <Form.Group className="mt-3">
            <Form.Label>{noteLabel}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={note}
              placeholder={notePlaceholder}
              onChange={(e) => setNote(e.target.value)}
              disabled={busy}
            />
          </Form.Group>
        )}
        {blocked && (
          <Alert variant="warning" className="mt-3 mb-0">
            {blocked}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" className="mt-3 mb-0">
            {error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onCancel} disabled={busy}>
          {blocked ? 'Close' : 'Cancel'}
        </Button>
        {!blocked && (
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={() => onConfirm(note)}
            disabled={busy || !typedOk}
          >
            {busy && <Spinner size="sm" className="me-2" />}
            {confirmLabel}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ConfirmDialog;
